/**
 * Asana API client - calls go through our backend which proxies to Asana
 * with the user's stored OAuth token from Key Vault.
 */

const API = import.meta.env.VITE_PUBLIC_API_URL || '/api'
const WORKSPACE_GID = '11089874356922' // danone.com

function createAsanaApi(getToken: () => Promise<string | null>) {
  async function asanaGet(path: string) {
    const token = await getToken()
    const res = await fetch(`${API}/asana${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) throw new Error(`Asana API error: ${res.status}`)
    const data = await res.json()
    return data.data
  }

  return {
    searchProjects: (query: string) =>
      asanaGet(`/workspaces/${WORKSPACE_GID}/typeahead?resource_type=project&query=${encodeURIComponent(query)}&count=20`),

    getProject: (gid: string) =>
      asanaGet(`/projects/${gid}`),

    // Pass projectId so the backend writes the synced fields with SYSTEM_ACTOR
    // and captures the change in the project's audit trail.
    async syncProject(gid: string, mappings: Record<string, string | null>, projectId?: string) {
      const token = await getToken()
      const res = await fetch(`${API}/asana/sync/${gid}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ mappings, ...(projectId ? { projectId } : {}) }),
      })
      if (!res.ok) throw new Error(`Sync error: ${res.status}`)
      return res.json()
    },

    async checkTemplate(gid: string) {
      const token = await getToken()
      const res = await fetch(`${API}/asana/projects/${gid}/template-check`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error(`Template check error: ${res.status}`)
      return res.json() as Promise<{ templateMatch: boolean; detail: Record<string, unknown> }>
    },

    async connect() {
      const token = await getToken()
      const res = await fetch(`${API}/asana/connect`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error('Failed to get Asana connect URL')
      return res.json()
    },

    async exchangeCode(code: string) {
      const token = await getToken()
      const res = await fetch(`${API}/asana/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ code }),
      })
      if (!res.ok) throw new Error('Token exchange failed')
      return res.json()
    },

    async disconnect() {
      const token = await getToken()
      await fetch(`${API}/asana/disconnect`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
    },

    async me() {
      const token = await getToken()
      const res = await fetch(`${API}/asana/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) return null
      return res.json() as Promise<{ name: string; email: string; gid: string } | null>
    },
  }
}

export type AsanaApi = ReturnType<typeof createAsanaApi>
export { createAsanaApi }
